/**
 * StepList Component
 * Displays a sortable list of steps using @dnd-kit for drag-and-drop reordering.
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { ScheduleStep, Schedule } from '../../types/schedule';
import { StepCard } from './StepCard';
import { scheduleService } from '../../services/scheduleService';
import { Action } from '../../types/action';

interface StepListProps {
  steps: ScheduleStep[];
  deviceId: string;
  scheduleId: string;
  deviceActions?: Action[];
  deviceSchedules?: Schedule[];  // For nested schedule info
  onAddStep: () => void;
  onEditStep: (step: ScheduleStep) => void;
  onDeleteStep: (step: ScheduleStep) => void;
}

export const StepList: React.FC<StepListProps> = ({
  steps,
  deviceId,
  scheduleId,
  deviceActions = [],
  deviceSchedules = [],
  onAddStep,
  onEditStep,
  onDeleteStep,
}) => {
  const { t } = useTranslation();
  const [reorderError, setReorderError] = useState<string | null>(null);

  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create a map of action ID to action for quick lookup
  const actionsMap = useMemo(() => {
    const map = new Map<string, Action>();
    deviceActions.forEach((action) => map.set(action.id, action));
    return map;
  }, [deviceActions]);

  // Create a map of schedule ID to schedule for quick lookup
  const schedulesMap = useMemo(() => {
    const map = new Map<string, Schedule>();
    deviceSchedules.forEach((schedule) => map.set(schedule.id, schedule));
    return map;
  }, [deviceSchedules]);

  // Handle drag end
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      // Find old and new indices
      const oldIndex = steps.findIndex((s) => s.id === active.id);
      const newIndex = steps.findIndex((s) => s.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      // Calculate new order
      const reorderedSteps = arrayMove(steps, oldIndex, newIndex);
      const newStepIds = reorderedSteps.map((s) => s.id);

      try {
        await scheduleService.reorderSteps(deviceId, scheduleId, newStepIds);
      } catch (err) {
        console.error('Failed to reorder steps:', err);
        setReorderError(t('schedules.errors.failedToReorderSteps'));
      }
    },
    [steps, deviceId, scheduleId, t]
  );

  if (steps.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          px: 2,
          textAlign: 'center',
          bgcolor: 'grey.50',
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'grey.300',
        }}
      >
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t('schedules.steps.noSteps')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddStep}
        >
          {t('schedules.steps.addStep')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={steps.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {steps.map((step) => {
              const action = step.actionId ? actionsMap.get(step.actionId) : undefined;
              const nestedSchedule = step.scheduleId ? schedulesMap.get(step.scheduleId) : undefined;
              return (
                <StepCard
                  key={step.id}
                  step={step}
                  onClick={() => onEditStep(step)}
                  onDelete={() => onDeleteStep(step)}
                  actionImage={action?.thumbnailUrl || action?.pictureUrl}
                  actionName={action?.name}
                  scheduleName={nestedSchedule?.name}
                  scheduleIcon={nestedSchedule?.icon?.emoji ?? undefined}
                />
              );
            })}
          </Box>
        </SortableContext>
      </DndContext>

      {/* Add step button */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddStep}
          fullWidth
        >
          {t('schedules.steps.addStep')}
        </Button>
      </Box>

      {/* Error snackbar */}
      <Snackbar
        open={!!reorderError}
        autoHideDuration={4000}
        onClose={() => setReorderError(null)}
      >
        <Alert severity="error" onClose={() => setReorderError(null)}>
          {reorderError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
